import { useCallback, useRef } from 'react'
import { toast } from 'sonner'
import type { QuoteBuilderApi } from '../api/client'
import { saveImportReview } from './api'
import type { ReviewDecision } from './review'
import type { CatalogMatch, ImportReview, ImportReviewUpdate, ImportRowReview, ReviewedRow } from './types'

// Review choices are saved as they are made, so an import reopens exactly as it was left (USA-1337).

/** The review choices the workspace holds, keyed by source row. */
export type ReviewState = {
  decisions: Record<string, ReviewDecision>
  matches: Record<string, number>
  manualMatches: Record<string, CatalogMatch>
  resolvedWarnings: Record<string, boolean>
  /** The product's chosen options (group → option id) and the accessories added under it (USA-1341). */
  rowOptions: Record<string, Record<string, string>>
  rowAccessories: Record<string, number[]>
}

/** A row's full saved choices; saving one replaces what was saved for that row. */
export function rowEntry(state: ReviewState, rowId: string): ImportRowReview {
  const picked = state.manualMatches[rowId]
  return {
    decision: state.decisions[rowId] ?? null,
    product_id: state.matches[rowId] ?? null,
    picked_product: picked ? { id: picked.id, manufacturer: picked.manufacturer, model: picked.model, title: picked.title } : null,
    warning_reviewed: state.resolvedWarnings[rowId] ?? false,
    configuration: state.rowOptions[rowId] ?? {},
    accessory_product_ids: state.rowAccessories[rowId] ?? [],
  }
}

export function rowEntries(state: ReviewState, rowIds: Iterable<string>) {
  return Object.fromEntries([...rowIds].map((rowId) => [rowId, rowEntry(state, rowId)]))
}

/** The saved choices over each row's starting match; rows never touched keep `defaultMatches`. */
export function restoreReview(
  rows: Array<Pick<ReviewedRow, 'row_id'>>,
  review: ImportReview | undefined,
  defaultMatches: Record<string, number>,
): ReviewState {
  const state: ReviewState = {
    decisions: {},
    matches: {},
    manualMatches: {},
    resolvedWarnings: {},
    rowOptions: {},
    rowAccessories: {},
  }
  for (const { row_id } of rows) {
    const saved = review?.rows?.[row_id]
    if (!saved) {
      if (defaultMatches[row_id] !== undefined) state.matches[row_id] = defaultMatches[row_id]
      continue
    }
    if (saved.decision) state.decisions[row_id] = saved.decision
    if (saved.product_id != null) state.matches[row_id] = saved.product_id
    if (saved.picked_product) state.manualMatches[row_id] = saved.picked_product
    if (saved.warning_reviewed) state.resolvedWarnings[row_id] = true
    if (saved.configuration && Object.keys(saved.configuration).length) state.rowOptions[row_id] = saved.configuration
    if (saved.accessory_product_ids?.length) state.rowAccessories[row_id] = saved.accessory_product_ids
  }
  return state
}

/** Later choices win: rows by row, the document-level fields when set. */
export function mergeReviewUpdates(earlier: ImportReviewUpdate, later: ImportReviewUpdate): ImportReviewUpdate {
  return {
    rows: { ...earlier.rows, ...later.rows },
    warnings_reviewed: later.warnings_reviewed ?? earlier.warnings_reviewed ?? null,
    match_mode: later.match_mode ?? earlier.match_mode ?? null,
  }
}

const SAVE_FAILED_TOAST = 'import-review-not-saved'

/**
 * Saves review choices one request at a time, in the order they were made. Choices made while a
 * save is running go out together next; a failed save is kept under newer choices and goes out
 * with the next choice or Retry, while a toast says so wherever the user has scrolled.
 */
export function useReviewSaver(api: QuoteBuilderApi) {
  const pending = useRef(new Map<string, ImportReviewUpdate>())
  const running = useRef(false)

  const flush = useCallback(async function flush() {
    if (running.current) return
    running.current = true
    try {
      for (let next = pending.current.entries().next(); !next.done; next = pending.current.entries().next()) {
        const [importId, update] = next.value
        pending.current.delete(importId)
        try {
          await saveImportReview(api, importId, update)
        } catch {
          const newer = pending.current.get(importId)
          pending.current.set(importId, newer ? mergeReviewUpdates(update, newer) : update)
          toast.error("Your last review choice wasn't saved.", {
            id: SAVE_FAILED_TOAST,
            duration: Infinity,
            action: { label: 'Retry', onClick: () => void flush() },
          })
          return
        }
      }
      toast.dismiss(SAVE_FAILED_TOAST)
    } finally {
      running.current = false
    }
  }, [api])

  const save = useCallback(
    (importId: string, update: ImportReviewUpdate) => {
      const queued = pending.current.get(importId)
      pending.current.set(importId, queued ? mergeReviewUpdates(queued, update) : update)
      void flush()
    },
    [flush],
  )

  return { save }
}
